/**
 * DOCUMENTATION:
 * Component: DesignPatternFundamentals
 * Purpose: Introduction to the taxonomy and history of software design patterns.
 * * * Note for Junior Developers:
 * - A pattern is not a library. It is a concept you implement in code.
 * - Anti-Pattern: A common response to a recurring problem that is usually ineffective
 * and risks being highly counterproductive. (e.g., "Big Ball of Mud").
 * - Design patterns are divided into Creational, Structural, and Behavioral categories.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CategoryBox = styled(Box)(({ theme, accent }) => ({
    padding: theme.spacing(2),
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "12px",
    borderLeft: `4px solid ${accent}`,
    height: "100%",
}));

export default function DesignPatternFundamentals() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const categories = useMemo(() => [
        {
            type: "Creational",
            accent: "#4ade80",
            desc: "Focus on object creation mechanisms, trying to create objects in a manner suitable to the situation.",
            examples: "Singleton, Factory, Builder"
        },
        {
            type: "Structural",
            accent: "#60a5fa",
            desc: "Focus on how classes and objects are composed to form larger structures.",
            examples: "Adapter, Decorator, Facade"
        },
        {
            type: "Behavioral",
            accent: "#a78bfa",
            desc: "Focus on communication between objects and how they assign responsibilities.",
            examples: "Observer, Strategy, State"
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    PATTERN TAXONOMY
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Why Patterns?
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Design patterns were popularized by the "Gang of Four" (GoF) in 1994.
                            They observed that experienced developers don't solve every problem from scratch.
                            Instead, they reuse solutions that have worked before. Patterns give us a
                            <strong> shared vocabulary</strong>. Instead of explaining how you are going
                            to sync data between two objects, you just say "I'll use the Observer pattern."
                        </Typography>

                        <Grid container spacing={3}>
                            {categories.map((cat, i) => (
                                <Grid item xs={12} md={4} key={i}>
                                    <CategoryBox accent={cat.accent}>
                                        <Typography variant="h6" sx={{ color: cat.accent, fontWeight: 800, mb: 1 }}>
                                            {cat.type}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                                            {cat.desc}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: 700 }}>
                                            KEY EXAMPLES: {cat.examples}
                                        </Typography>
                                    </CategoryBox>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/design-patterns-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
